import { useRef, useState } from "react";
import { Button, Spin } from "antd";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OnboardingProvider, useOnboarding } from "../../../contexts/OnboardingContext";
import { useToast } from "../../../contexts/ToastContext";
import OnboardingSidebar from "./OnboardingSidebar";
import CompanyInformation from "./steps/CompanyInformation";
import Categories from "./steps/Categories";
import AddCar from "./steps/AddCar";
import PaymentInsurance from "./steps/PaymentInsurance";
import Preferences from "./steps/Preferences";
import { markPostOnboardingSetupPending } from "../../../components/product-tour/postOnboardingSetupStorage";

const OnboardingWizardContent = () => {
    const { t } = useTranslation("onboarding");
    const history = useHistory();
    const toast = useToast();
    const {
        currentStepKey,
        isStepResolved,
        loading,
        goToNextStep,
        goToPreviousStep,
        completeOnboarding,
    } = useOnboarding();

    const companyInfoRef = useRef(null);
    const categoriesRef = useRef(null);
    const paymentInsuranceRef = useRef(null);
    const preferencesRef = useRef(null);
    const [actionLoading, setActionLoading] = useState(false);
    const steps = [
        { component: CompanyInformation, key: "company-info", ref: companyInfoRef },
        { component: Categories, key: "categories", ref: categoriesRef },
        { component: AddCar, key: "add-car", ref: null },
        { component: PaymentInsurance, key: "stripe", ref: paymentInsuranceRef },
        { component: Preferences, key: "preferences", ref: preferencesRef },
    ];

    const activeIndex = Math.max(
        steps.findIndex((s) => s.key === currentStepKey),
        0,
    );

    const isLastStep = activeIndex === steps.length - 1;
    const isFirstStep = activeIndex === 0;
    const CurrentStepComponent = steps[activeIndex]?.component ?? steps[0].component;

    const handleComplete = async () => {
        const result = await completeOnboarding();
        if (result.success) {
            toast.success({ message: t("messages.completionSuccess") });
            markPostOnboardingSetupPending();
            setTimeout(() => history.push("/"), 1000);
        } else {
            toast.error({
                message: t("messages.completionError"),
                description: result.error,
            });
        }
    };

    const handleContinue = async () => {
        if (isLastStep) {
            // Preferences is now the final step, so its pending edits have to be saved before
            // onboarding is marked complete — otherwise Continue discards them.
            if (currentStepKey === "preferences" && preferencesRef.current?.submit) {
                setActionLoading(true);
                try {
                    const saved = await preferencesRef.current.submit();
                    if (!saved) return;
                } finally {
                    setActionLoading(false);
                }
            }
            await handleComplete();
            return;
        }

        setActionLoading(true);
        try {
            if (activeIndex === 0 && companyInfoRef.current?.submit) {
                const saved = await companyInfoRef.current.submit();
                if (saved) goToNextStep();
                return;
            }

            if (currentStepKey === "categories" && categoriesRef.current?.submit) {
                const saved = await categoriesRef.current.submit();
                if (saved) goToNextStep();
                return;
            }

            if (currentStepKey === "stripe") {
                const canContinue = paymentInsuranceRef.current?.validateForContinue?.();
                if (!canContinue) {
                    return;
                }
                goToNextStep();
                return;
            }

            goToNextStep();
        } finally {
            setActionLoading(false);
        }
    };

    const handleBack = () => {
        goToPreviousStep();
    };

    const handleSkipStep = () => {
        if (isLastStep) {
            return;
        }
        goToNextStep();
    };

    const continueLabel = isLastStep ? t("navigation.finish") : t("navigation.continue");

    return (
        <div className="min-h-screen bg-[#F4F4FB] p-4 sm:p-6">
            <div className="mx-auto flex max-w-[1280px] flex-col gap-4 lg:flex-row lg:items-stretch">
                <aside className="order-1 w-full shrink-0 lg:order-2 lg:w-[320px] lg:self-start lg:sticky lg:top-6">
                    <OnboardingSidebar className="w-full" />
                </aside>

                <main className="order-2 flex min-w-0 flex-1 flex-col gap-3 lg:order-1">
                    <div className="flex flex-1 flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8">
                        <div className="flex min-h-[min(500px,70vh)] flex-1 flex-col">
                            {!isStepResolved ? (
                                <div className="flex flex-1 items-center justify-center">
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <CurrentStepComponent ref={steps[activeIndex]?.ref ?? undefined} />
                            )}
                        </div>

                        <div className="mt-8 flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                            {!isLastStep && !isFirstStep && currentStepKey !== "stripe" ? (
                                <Button
                                    size="large"
                                    onClick={handleSkipStep}
                                    disabled={loading || actionLoading || !isStepResolved}
                                    className="h-11 min-w-[96px] rounded-lg border-gray-200 text-gray-700">
                                    {t("navigation.skip")}
                                </Button>
                            ) : (
                                <span />
                            )}

                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                {!isFirstStep ? (
                                    <Button
                                        size="large"
                                        onClick={handleBack}
                                        disabled={loading || actionLoading || !isStepResolved}
                                        className="h-11 min-w-[96px] rounded-lg border-gray-200 text-gray-700">
                                        {t("navigation.back")}
                                    </Button>
                                ) : null}

                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={handleContinue}
                                    loading={loading || actionLoading}
                                    disabled={!isStepResolved}
                                    className="h-11 min-w-[120px] rounded-lg border-none bg-[#5d5dff] hover:!bg-[#4e4ee8]">
                                    {continueLabel}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <p className="pb-2 text-center text-xs text-gray-500">
                        &copy; SoftCar | {new Date().getFullYear()}
                    </p>
                </main>
            </div>

        </div>
    );
};

const OnboardingWizard = () => (
    <OnboardingProvider>
        <OnboardingWizardContent />
    </OnboardingProvider>
);

export default OnboardingWizard;
